import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../Components/Common/breadcrumbs";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  useTheme,
} from "@mui/material";
import { Download, People, TrendingUp, WhatsApp } from "@mui/icons-material";
import Axios from "../../Common/api/axiosConfig";

const Analytics = () => {
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState([
    { label: "Store Views", value: 0, color: "#1E88E5" },
    { label: "WhatsApp Clicks", value: 0, color: "#43A047" },
    { label: "Total Orders", value: 0, color: "#8E24AA" },
    { label: "Conversion Rate", value: "0%", color: "#FB8C00" },
  ]);

  const [storePerformance, setStorePerformance] = useState([
    { label: "Page Views Today", value: 0 },
    { label: "WhatsApp Clicks Today", value: 0 },
    { label: "Orders Today", value: 0 },
    { label: "Revenue Today", value: "₹0" },
  ]);

  const [customerInsights, setCustomerInsights] = useState([
    { label: "Avg. Session Duration", value: "3m 45s" },
    {
      label: "Most Viewed Product",
      value:
        'Refurbished Dell Latitude 5410 (Core i5 10th Gen/16GB/512GB SSD/Webcam/14" Non Touch/WIN)',
    },
    { label: "Peak Hours", value: "10 AM - 2 PM" },
    { label: "Mobile Visitors", value: "85%" },
  ]);

  const tools = [
    { label: "Export Data", icon: <Download fontSize="large" /> },
    { label: "View Trends", icon: <TrendingUp fontSize="large" /> },
    { label: "Customer Report", icon: <People fontSize="large" /> },
    { label: "WhatsApp Stats", icon: <WhatsApp fontSize="large" /> },
  ];

  const fetchAnalyticsData = async () => {
    try {
      const response = await Axios.get("/analytics");
      const data = response.data?.data?.[0];

      if (data) {
        // Update analytics summary cards
        setAnalyticsData([
          {
            label: "Store Views",
            value: data.pageViews || 0,
            color: "#1E88E5",
          },
          {
            label: "WhatsApp Clicks",
            value: data.whatsappClicks || 0,
            color: "#43A047",
          },
          { label: "Total Orders", value: data.orders || 0, color: "#8E24AA" },
          {
            label: "Conversion Rate",
            value:
              data.pageViews > 0
                ? `${((data.orders / data.pageViews) * 100).toFixed(1)}%`
                : "0%",
            color: "#FB8C00",
          },
        ]);

        // Update store performance details
        setStorePerformance([
          { label: "Page Views Today", value: data.pageViews || 0 },
          { label: "WhatsApp Clicks Today", value: data.whatsappClicks || 0 },
          { label: "Orders Today", value: data.orders || 0 },
          {
            label: "Revenue Today",
            value: `₹${data.revenue?.toLocaleString() || 0}`,
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  return (
    <>
      <Breadcrumbs />

      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Track your store performance and customer engagement
        </Typography>

        {/* 4 Analytics Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {analyticsData.map((item) => (
            <Grid item xs={6} md={3} key={item.label}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.05)",
                  height: "100%",
                  textAlign: "center",
                  py: 2,
                  px: 1,
                }}
              >
                <CardContent sx={{ p: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: item.color,
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Store Performance + Customer Insights */}
        <Grid container spacing={3}>
          {/* Store Performance */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Store Performance
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Track your store metrics over time
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {storePerformance.map((item) => (
                  <Box key={item.label} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Insights */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Customer Insights
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Understand your customers better
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {customerInsights.map((item) => (
                  <Box key={item.label} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Analytics Tools Card */}
      <Card
        sx={{
          mt: 4,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Analytics Tools
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Advanced tools for tracking performance
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {tools.map((tool) => (
              <Grid item xs={6} md={3} key={tool.label}>
                <Button
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 2,
                    borderRadius: 2,
                    width: "100%",
                    textTransform: "none",
                    minHeight: "100px",
                  }}
                >
                  {tool.icon}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {tool.label}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default Analytics;
